/**
 * Комната в шапке.
 *
 * Комната живёт дольше партии: собрались один раз, а квестов сыграли сколько
 * захотели. Поэтому её место — не отдельная страница, а шапка: она должна быть
 * видна и в каталоге, где хозяин выбирает следующий квест, и в профиле, и на
 * странице квеста. Свёрнутая она занимает одну кнопку со счётчиком, раскрытая
 * показывает код приглашения и состав.
 *
 * Открытая вкладка удерживает присутствие в комнате: пока жив хотя бы один
 * сокет, участник считается на связи. Поэтому виджет держит соединение всегда,
 * а не только когда панель раскрыта.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import {
  queryKeys,
  useCreateRoom,
  useJoinRoom,
  useLeaveRoom,
  useMyRoom,
  useRemoveRoomMember,
} from "../api/queries";
import { play } from "../lib/sound";
import { Avatar } from "./Avatar";
import { PlayerLink } from "./PlayerLink";
import { useAuthStore } from "../stores/authStore";
import type { SessionEvent } from "../ws/events";
import { useRoomSocket } from "../ws/useSessionSocket";

export function RoomWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // Поле кода спрятано за кнопкой: чаще всего человек своей комнатой и
  // пользуется, а переход в чужую — редкое действие.
  const [joining, setJoining] = useState(false);
  const [code, setCode] = useState("");
  const box = useRef<HTMLDivElement>(null);

  const room = useMyRoom().data;
  const createRoom = useCreateRoom();
  const leaveRoom = useLeaveRoom();
  const joinRoom = useJoinRoom();
  const removeMember = useRemoveRoomMember();
  const me = useAuthStore((state) => state.user);

  // Звучит только прибавление: уход и исключение — не повод отвлекать
  // остальных, а первая загрузка комнаты не «приход» вовсе.
  const memberCount = room?.members?.length ?? 0;
  const knownCount = useRef<number | null>(null);
  useEffect(() => {
    if (!room?.id) {
      knownCount.current = null;
      return;
    }
    if (knownCount.current !== null && memberCount > knownCount.current) {
      play("roomJoin");
    }
    knownCount.current = memberCount;
  }, [room?.id, memberCount]);

  const handleEvent = useCallback(
    (event: SessionEvent) => {
      if (event.type === "room_updated") {
        // Состав изменился — чаще всего кто-то пришёл. Звук отдан эффекту
        // ниже, который сравнивает численность: событие приходит и на уход,
        // и на исключение, а «кто-то присоединился» — только про приход.
        void queryClient.invalidateQueries({ queryKey: queryKeys.myRoom() });
      }
      if (event.type === "room_game_started") {
        // Комната начала партию — участника забирает в игру, где бы он ни был.
        void queryClient.invalidateQueries({ queryKey: queryKeys.myRoom() });
        navigate(`/play/${event.payload.sessionId}`);
      }
    },
    [navigate, queryClient],
  );

  useRoomSocket(room?.id, { onEvent: handleEvent });

  useEffect(() => {
    if (!open) return;

    const close = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const members = room?.members ?? [];
  const iAmHost = members.some((member) => member.isHost && member.user?.id === me?.id);
  const onlineCount = members.filter((member) => member.online).length;
  const inviteLink = room ? `${window.location.origin}/join/${room.joinCode ?? ""}` : "";

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Буфер обмена недоступен — ссылка видна в поле рядом.
    }
  };

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        className={`btn-ghost gap-1.5 px-2.5 ${room ? "text-amber-300" : ""}`}
        onClick={() => {
          if (room) {
            setOpen((value) => !value);
            return;
          }
          createRoom.mutate(undefined, { onSuccess: () => play("roomOpen") });
        }}
        disabled={createRoom.isPending}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={room ? t("room.title") : t("room.create")}
      >
        <RoomIcon />
        {room ? (
          <span className="tabular-nums text-xs">
            {onlineCount}/{members.length}
          </span>
        ) : (
          <span className="hidden text-xs sm:inline">{t("room.create")}</span>
        )}
      </button>

      {open && room && (
        <div
          role="dialog"
          aria-label={t("room.title")}
          className="absolute right-0 top-full z-40 mt-1 w-[19rem] overflow-hidden rounded-xl
                     border border-ink-700 bg-ink-900 shadow-panel"
        >
          <div className="border-b border-ink-800 px-4 py-3">
            <span className="label mb-1">{t("room.inviteCode")}</span>
            <div className="tabular-nums text-2xl tracking-[0.3em] text-amber-300">
              {room.joinCode}
            </div>

            <div className="mt-2 flex gap-2">
              <input className="field py-1.5 text-[11px]" readOnly value={inviteLink} />
              <button
                type="button"
                className="btn-secondary shrink-0 px-2.5 py-1.5 text-xs"
                onClick={() => void copyInvite()}
              >
                {copied ? t("lobby.copied") : t("lobby.copy")}
              </button>
            </div>
          </div>

          <ul className="max-h-56 overflow-y-auto px-2 py-2">
            {members.map((member) => (
              <li
                key={member.user?.id}
                className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm"
              >
                <span className="relative shrink-0">
                  <Avatar
                    url={member.user?.avatarUrl}
                    username={member.user?.username ?? ""}
                    role={member.user?.role}
                    className={`h-7 w-7 text-xs ${member.online ? "" : "opacity-40"}`}
                  />
                  {/* Точка присутствия: закрытая вкладка не выводит из
                      состава, но и ждать такого участника комната не будет. */}
                  <span
                    className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full
                                border-2 border-ink-900 ${member.online ? "bg-sage" : "bg-ink-600"}`}
                  />
                </span>
                <PlayerLink
                  userId={member.user?.id}
                  username={member.user?.username}
                  className={`flex-1 truncate ${member.online ? "text-ink-100" : "text-ink-500"}`}
                />
                {member.isHost && (
                  <span className="chip border-amber-400/40 text-amber-300">{t("lobby.host")}</span>
                )}

                {/* Освободить место за тем, кто закрыл вкладку и не вернулся.
                    Себя хозяин так не убирает: для этого есть выход, который
                    закрывает комнату целиком. */}
                {iAmHost && !member.isHost && (
                  <button
                    type="button"
                    className="btn-ghost px-1.5 py-0.5 text-xs leading-none text-ink-500 hover:text-rust"
                    disabled={removeMember.isPending}
                    aria-label={t("room.remove", { name: member.user?.username ?? "" })}
                    title={t("room.remove", { name: member.user?.username ?? "" })}
                    onClick={() =>
                      removeMember.mutate({ roomId: room.id!, userId: member.user!.id! })
                    }
                  >
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>

          <div className="border-t border-ink-800 px-4 py-3">
            <p className="text-xs text-ink-400">
              {room.currentSessionId ? t("room.playing") : t("room.pickQuestHint")}
            </p>
            {/* Переход в чужую комнату. Своя при этом закрывается: состоять
                в двух сразу нельзя, и сервер поступает так же. Об этом
                сказано прямо — код вводят, не думая о своей комнате. */}
            {joining ? (
              <form
                className="mt-2 space-y-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  const value = code.trim().toUpperCase();
                  if (!value) return;
                  joinRoom.mutate(value, {
                    onSuccess: () => {
                      play("roomOpen");
                      setJoining(false);
                      setCode("");
                    },
                  });
                }}
              >
                <input
                  className="field text-center tabular-nums tracking-[0.25em] uppercase"
                  placeholder={t("room.codePlaceholder")}
                  value={code}
                  maxLength={8}
                  autoFocus
                  onChange={(event) => setCode(event.target.value.toUpperCase())}
                />
                <p className="text-[11px] text-ink-500">
                  {iAmHost ? t("room.joinWarningHost") : t("room.joinWarning")}
                </p>
                {joinRoom.isError && (
                  <p className="text-xs text-rust">
                    {joinRoom.error instanceof Error ? joinRoom.error.message : t("room.joinFailed")}
                  </p>
                )}
                <div className="flex gap-2">
                  <button type="submit" className="btn-primary flex-1 py-2 text-xs" disabled={joinRoom.isPending}>
                    {t("room.joinSubmit")}
                  </button>
                  <button
                    type="button"
                    className="btn-ghost px-3 py-2 text-xs"
                    onClick={() => {
                      setJoining(false);
                      setCode("");
                    }}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </form>
            ) : (
              <button
                type="button"
                className="btn-secondary mt-2 w-full py-2 text-xs"
                onClick={() => setJoining(true)}
              >
                {t("room.joinOther")}
              </button>
            )}

            <button
              type="button"
              className="btn-ghost mt-2 w-full text-rust hover:bg-rust/10"
              disabled={leaveRoom.isPending}
              onClick={() =>
                leaveRoom.mutate(room.id!, {
                  onSuccess: () => setOpen(false),
                })
              }
            >
              {iAmHost ? t("room.close") : t("room.leave")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function RoomIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path d="M16 19v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 17.5V19" strokeLinecap="round" />
      <circle cx="10" cy="8" r="3" />
      <path d="M20 19v-1.5a3.5 3.5 0 0 0-2.6-3.4M15.5 5.3a3 3 0 0 1 0 5.4" strokeLinecap="round" />
    </svg>
  );
}
